import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  Edit2, 
  Trash2, 
  ArrowLeft,
  Mail,
  Phone,
  User,
  Eye,
  Layers
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0
  }).format(value || 0);
};

export default function AdminEmpresas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    nombre_empresa: '',
    contacto_nombre: '',
    contacto_email: '',
    contacto_telefono: '',
    activo: true
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadEmpresas();
  }, [user, navigate]);

  const loadEmpresas = async () => {
    try {
      const res = await api.get('/empresas');
      setEmpresas(res.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre_empresa: '',
      contacto_nombre: '',
      contacto_email: '',
      contacto_telefono: '',
      activo: true
    });
    setEditingEmpresa(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmpresa) {
        await api.put(`/empresas/${editingEmpresa.id}`, formData);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/empresas', formData);
        toast.success('Empresa creada');
      }
      setDialogOpen(false);
      resetForm();
      loadEmpresas();
    } catch (error) {
      toast.error('Error al guardar empresa');
    }
  };

  const handleEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre_empresa: empresa.nombre_empresa,
      contacto_nombre: empresa.contacto_nombre || '',
      contacto_email: empresa.contacto_email || '',
      contacto_telefono: empresa.contacto_telefono || '',
      activo: empresa.activo
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta empresa? Se eliminarán todas sus marcas y datos.')) return;
    try {
      await api.delete(`/empresas/${id}`);
      toast.success('Empresa eliminada');
      loadEmpresas();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div data-testid="admin-empresas">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>Empresas</h1>
              <p className="text-xs text-muted-foreground">Clientes de FinScope</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nombre de la Empresa *</Label>
                  <Input
                    value={formData.nombre_empresa}
                    onChange={(e) => setFormData({...formData, nombre_empresa: e.target.value})}
                    placeholder="Ej: HAUS"
                    required
                  />
                </div>
                <div>
                  <Label>Contacto Principal</Label>
                  <Input
                    value={formData.contacto_nombre}
                    onChange={(e) => setFormData({...formData, contacto_nombre: e.target.value})}
                    placeholder="Nombre del contacto"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.contacto_email}
                    onChange={(e) => setFormData({...formData, contacto_email: e.target.value})}
                    placeholder="contacto@empresa.com"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.contacto_telefono}
                    onChange={(e) => setFormData({...formData, contacto_telefono: e.target.value})}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="activo">Empresa activa</Label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingEmpresa ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : empresas.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sin empresas</h3>
            <p className="text-muted-foreground mb-4">Crea tu primer cliente de FinScope</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Empresa
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {empresas.map((empresa) => (
              <Card key={empresa.id} className={!empresa.activo ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${empresa.activo ? 'bg-primary/10' : 'bg-slate-100'}`}>
                        <Building2 className={`w-6 h-6 ${empresa.activo ? 'text-primary' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{empresa.nombre_empresa}</h3>
                          {!empresa.activo && <Badge variant="secondary">Inactiva</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            {empresa.num_marcas || 0} marcas
                          </span>
                          {empresa.contacto_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {empresa.contacto_email}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm font-bold text-green-600">
                          {formatCurrency(empresa.ingreso_mensual)}/mes
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/admin/empresas/${empresa.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(empresa)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(empresa.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
